import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, useAuth } from '../contexts/AuthContext';

const STATUS_LABEL = {
  disconnected: 'Desconectado',
  qrcode: 'Aguardando QR',
  connecting: 'Conectando...',
  connected: 'Conectado',
  banned: 'Banido',
  error: 'Erro',
};

const STATUS_COLOR = {
  disconnected: '#5E7A82',
  qrcode: '#fe8d25',
  connecting: '#fe8d25',
  connected: '#4CB553',
  banned: '#b43737',
  error: '#b43737',
};

function StatusBadge({ status }) {
  const cor = STATUS_COLOR[status] || '#5E7A82';
  return (
    <span
      className="badge"
      style={{
        background: `${cor}22`,
        color: cor,
        border: `1px solid ${cor}55`,
        fontWeight: 600,
        padding: '4px 10px',
      }}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function formatNumero(numero) {
  if (!numero) return '—';
  const limpo = String(numero).replace(/\D/g, '');
  if (limpo.length === 13) {
    return `+${limpo.slice(0, 2)} (${limpo.slice(2, 4)}) ${limpo.slice(4, 9)}-${limpo.slice(9)}`;
  }
  return numero;
}

export default function AdminWhatsapp() {
  const { temPermissao } = useAuth();
  const [canais, setCanais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [qrModal, setQrModal] = useState(null); // { canalId, base64, pairing, status, atualizadoEm }
  const [fluxosDisp, setFluxosDisp] = useState([]); // fluxos publicados atribuiveis
  const pollRef = useRef(null);

  const podeAdmin = temPermissao('admin:whatsapp');

  const carregar = async () => {
    try {
      const [res, resF] = await Promise.all([
        axios.get(`${API}/canais`),
        axios.get(`${API}/canais/fluxos-disponiveis`).catch(() => ({ data: { data: [] } })),
      ]);
      setCanais(res.data?.data || []);
      setFluxosDisp(resF.data?.data || []);
      setErro('');
    } catch (err) {
      setErro('Nao foi possivel carregar os canais.');
    } finally {
      setLoading(false);
    }
  };

  const atribuirFluxo = async (canal, versaoId) => {
    setSalvando(true);
    try {
      await axios.put(`${API}/canais/${canal.id}/fluxo-versao`, {
        fluxo_versao_id: versaoId || null,
      });
      await carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || err.message });
    } finally {
      setSalvando(false);
    }
  };

  useEffect(() => {
    if (!podeAdmin) {
      setLoading(false);
      return undefined;
    }
    carregar();
  }, [podeAdmin]);

  // Polling enquanto houver canal em qrcode/connecting OU modal de QR aberto
  const precisaPolling = useMemo(
    () => qrModal !== null || canais.some((c) => c.status === 'qrcode' || c.status === 'connecting'),
    [canais, qrModal],
  );

  useEffect(() => {
    if (!precisaPolling) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    if (pollRef.current) return undefined;
    pollRef.current = setInterval(async () => {
      await carregar();
      if (qrModal) {
        try {
          const res = await axios.get(`${API}/canais/${qrModal.canalId}/qrcode`);
          setQrModal((prev) => prev && {
            ...prev,
            base64: res.data?.data?.base64,
            pairing: res.data?.data?.pairing_code,
            status: res.data?.data?.status,
            atualizadoEm: res.data?.data?.atualizado_em,
          });
        } catch (err) {
          // QR pode ter expirado/canal desconectado - so o status do canal vai contar
        }
      }
    }, 3000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [precisaPolling, qrModal?.canalId]);

  // Fecha modal automaticamente quando o canal pareia
  useEffect(() => {
    if (!qrModal) return;
    const canal = canais.find((c) => c.id === qrModal.canalId);
    if (canal?.status === 'connected') {
      setQrModal(null);
      Swal.fire({
        icon: 'success',
        title: 'Numero pareado!',
        text: `${canal.nome} esta conectado.`,
        timer: 2000,
        showConfirmButton: false,
      });
    }
  }, [canais, qrModal]);

  const novoCanal = async () => {
    const { value: nome } = await Swal.fire({
      title: 'Novo canal WhatsApp',
      input: 'text',
      inputLabel: 'Nome do canal (ex.: Atendimento TI, Numero de Teste)',
      inputPlaceholder: 'Atendimento TI',
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: 'Criar',
      cancelButtonText: 'Cancelar',
      inputValidator: (value) => {
        if (!value || value.trim().length < 3) return 'Nome precisa ter pelo menos 3 caracteres.';
        return null;
      },
    });
    if (!nome) return;

    setSalvando(true);
    try {
      await axios.post(`${API}/canais`, { nome: nome.trim() });
      await carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro ao criar canal', text: err.response?.data?.message || err.message });
    } finally {
      setSalvando(false);
    }
  };

  const conectar = async (canal) => {
    setSalvando(true);
    try {
      const res = await axios.post(`${API}/canais/${canal.id}/connect`);
      const atualizado = res.data?.data;

      // Abre o modal ja, mesmo sem QR ainda - o polling preenche assim que o
      // webhook qrcode.updated chegar do Evolution.
      setQrModal({
        canalId: canal.id,
        nome: canal.nome,
        base64: atualizado?.qrcode_base64 || null,
        pairing: atualizado?.qrcode_pairing_code || null,
        status: atualizado?.status || 'qrcode',
        atualizadoEm: atualizado?.qrcode_atualizado_em,
      });

      try {
        const qrRes = await axios.get(`${API}/canais/${canal.id}/qrcode`);
        setQrModal((prev) => prev && {
          ...prev,
          base64: qrRes.data?.data?.base64 || prev.base64,
          pairing: qrRes.data?.data?.pairing_code || prev.pairing,
          status: qrRes.data?.data?.status || prev.status,
          atualizadoEm: qrRes.data?.data?.atualizado_em || prev.atualizadoEm,
        });
      } catch (qrErr) {
        // QR ainda nao gerado - polling vai pegar quando chegar.
      }

      await carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro ao conectar', text: err.response?.data?.message || err.message });
    } finally {
      setSalvando(false);
    }
  };

  const desconectar = async (canal) => {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Desconectar canal?',
      text: `O numero "${canal.nome}" vai parar de receber mensagens ate parear novamente.`,
      showCancelButton: true,
      confirmButtonText: 'Desconectar',
      cancelButtonText: 'Cancelar',
    });
    if (!r.isConfirmed) return;

    setSalvando(true);
    try {
      await axios.post(`${API}/canais/${canal.id}/disconnect`);
      await carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro ao desconectar', text: err.response?.data?.message || err.message });
    } finally {
      setSalvando(false);
    }
  };

  const tornarAtivo = async (canal) => {
    setSalvando(true);
    try {
      await axios.post(`${API}/canais/${canal.id}/active`);
      await carregar();
      Swal.fire({
        icon: 'success',
        title: 'Canal ativo',
        text: `${canal.nome} agora recebe os atendimentos.`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || err.message });
    } finally {
      setSalvando(false);
    }
  };

  const toggleFluxo = async (canal) => {
    setSalvando(true);
    try {
      await axios.patch(`${API}/canais/${canal.id}/fluxo`, { fluxo_ativo: !canal.fluxo_ativo });
      await carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || err.message });
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (canal) => {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Excluir canal?',
      text: `O canal "${canal.nome}" e a sessao no Evolution serao removidos. Esta acao nao pode ser desfeita.`,
      showCancelButton: true,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b43737',
    });
    if (!r.isConfirmed) return;

    setSalvando(true);
    try {
      await axios.delete(`${API}/canais/${canal.id}`);
      await carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro ao excluir', text: err.response?.data?.message || err.message });
    } finally {
      setSalvando(false);
    }
  };

  if (!podeAdmin) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">Voce nao tem permissao para gerenciar canais WhatsApp.</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="mb-1" style={{ color: 'var(--portal-text)' }}>Canais WhatsApp</h3>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Gerencie os numeros usados para receber atendimentos. So um canal fica ativo por vez.
          </p>
        </div>
        <button
          className="btn"
          onClick={novoCanal}
          disabled={salvando}
          style={{
            background: 'linear-gradient(135deg, #2A8A9C 0%, #1E6370 100%)',
            color: '#fff',
            fontWeight: 600,
            border: 'none',
            padding: '8px 18px',
          }}
        >
          <i className="bi bi-plus-lg me-2" /> Novo canal
        </button>
      </div>

      {erro && <div className="alert alert-danger">{erro}</div>}

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border" /></div>
      ) : canais.length === 0 ? (
        <div className="card p-4 text-center">
          <p className="mb-0 text-muted">Nenhum canal cadastrado. Clique em "Novo canal" para comecar.</p>
        </div>
      ) : (
        <div className="row g-3">
          {canais.map((canal) => (
            <div key={canal.id} className="col-12 col-md-6 col-lg-4">
              <div
                className="card h-100 p-3"
                style={{
                  border: canal.ativo ? '2px solid #2A8A9C' : '1px solid var(--portal-border)',
                  position: 'relative',
                }}
              >
                {canal.ativo && (
                  <span
                    className="badge"
                    style={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      background: '#2A8A9C',
                      color: '#fff',
                      fontWeight: 600,
                    }}
                  >
                    ATIVO
                  </span>
                )}
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="bi bi-whatsapp" style={{ color: '#25D366', fontSize: '1.4rem' }} />
                  <h5 className="mb-0" style={{ color: 'var(--portal-text)' }}>{canal.nome}</h5>
                </div>
                <div className="mb-2"><StatusBadge status={canal.status} /></div>
                <div style={{ fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                  <div><strong>Numero:</strong> {formatNumero(canal.numero)}</div>
                  <div><strong>Instance:</strong> <code>{canal.instance_name}</code></div>
                  {canal.conectado_em && (
                    <div><strong>Conectado em:</strong> {new Date(canal.conectado_em).toLocaleString('pt-BR')}</div>
                  )}
                  {canal.ultimo_erro && (
                    <div className="text-danger"><strong>Erro:</strong> {canal.ultimo_erro}</div>
                  )}
                </div>

                <div
                  className="d-flex align-items-center gap-2 mt-3"
                  style={{ fontSize: '0.82rem', borderTop: '1px solid var(--portal-border)', paddingTop: 10 }}
                >
                  <button
                    type="button"
                    onClick={() => toggleFluxo(canal)}
                    disabled={salvando}
                    style={{
                      width: 38,
                      height: 22,
                      borderRadius: 11,
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      background: canal.fluxo_ativo ? '#2A8A9C' : '#666',
                      position: 'relative',
                      flexShrink: 0,
                      transition: 'background 0.2s',
                    }}
                    title={canal.fluxo_ativo ? 'Desligar o bot neste canal' : 'Ligar o bot neste canal'}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: 3,
                        left: canal.fluxo_ativo ? 18 : 3,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: '#fff',
                        transition: 'left 0.2s',
                      }}
                    />
                  </button>
                  <span style={{ color: canal.fluxo_ativo ? 'var(--portal-text)' : 'var(--portal-muted)' }}>
                    Bot do canal: {canal.fluxo_ativo ? <strong>ligado</strong> : <strong>desligado</strong>}
                  </span>
                </div>

                <div className="mt-2">
                  <label className="form-label mb-1" style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Fluxo do bot (construtor)
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={canal.fluxo_versao_id || ''}
                    onChange={(e) => atribuirFluxo(canal, e.target.value ? Number(e.target.value) : null)}
                    disabled={salvando}
                    title="Fluxo construido no editor que roda neste canal"
                  >
                    <option value="">Bot padrao (legado)</option>
                    {fluxosDisp.map((f) => (
                      <option key={f.versao_id} value={f.versao_id}>{f.nome} (v{f.versao})</option>
                    ))}
                  </select>
                  <div className="mt-1" style={{ fontSize: '0.74rem' }}>
                    {!canal.fluxo_ativo ? (
                      <span style={{ color: '#e0a800' }}>
                        <i className="bi bi-exclamation-triangle me-1" />
                        Bot desligado — nenhuma mensagem aciona o bot, mesmo com um fluxo escolhido.
                      </span>
                    ) : canal.fluxo_versao_id ? (
                      <span style={{ color: '#46b450' }}>
                        <i className="bi bi-check-circle me-1" />
                        Rodando: {canal.fluxo_nome ? `${canal.fluxo_nome} v${canal.fluxo_versao}` : `fluxo v${canal.fluxo_versao_id}`}
                      </span>
                    ) : (
                      <span className="text-muted">
                        <i className="bi bi-robot me-1" />
                        Rodando: bot padrão (legado)
                      </span>
                    )}
                  </div>
                </div>

                <div className="d-flex gap-2 mt-3 flex-wrap">
                  {canal.status !== 'connected' && (
                    <button
                      className="btn btn-sm"
                      onClick={() => conectar(canal)}
                      disabled={salvando}
                      style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
                    >
                      <i className="bi bi-qr-code me-1" /> Conectar
                    </button>
                  )}
                  {canal.status === 'connected' && !canal.ativo && (
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => tornarAtivo(canal)}
                      disabled={salvando}
                    >
                      <i className="bi bi-check2-circle me-1" /> Tornar ativo
                    </button>
                  )}
                  {canal.status === 'connected' && (
                    <button
                      className="btn btn-sm btn-outline-warning"
                      onClick={() => desconectar(canal)}
                      disabled={salvando}
                    >
                      <i className="bi bi-power me-1" /> Desconectar
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => excluir(canal)}
                    disabled={salvando}
                  >
                    <i className="bi bi-trash me-1" /> Excluir
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {qrModal && (
        <div
          className="modal d-block"
          style={{ background: 'rgba(0,0,0,0.7)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: 'var(--portal-surface)', color: 'var(--portal-text)' }}>
              <div className="modal-header" style={{ borderColor: 'var(--portal-border)' }}>
                <h5 className="modal-title">Parear {qrModal.nome}</h5>
                <button className="btn-close btn-close-white" onClick={() => setQrModal(null)} />
              </div>
              <div className="modal-body text-center">
                {qrModal.base64 ? (
                  <>
                    <img
                      src={qrModal.base64.startsWith('data:') ? qrModal.base64 : `data:image/png;base64,${qrModal.base64}`}
                      alt="QR code"
                      style={{ width: 280, height: 280, background: '#fff', padding: 12, borderRadius: 12 }}
                    />
                    {qrModal.pairing && (
                      <p className="mt-3 mb-1" style={{ fontSize: '0.85rem', color: 'var(--portal-muted)' }}>
                        Ou use o codigo de pareamento:
                      </p>
                    )}
                    {qrModal.pairing && (
                      <code style={{ fontSize: '1.2rem', letterSpacing: 2 }}>{qrModal.pairing}</code>
                    )}
                    <p className="mt-3 mb-0" style={{ fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                      No celular: WhatsApp &gt; Aparelhos conectados &gt; Conectar um aparelho.
                      <br />O QR e atualizado a cada poucos segundos automaticamente.
                    </p>
                  </>
                ) : (
                  <div className="py-4">
                    <div className="spinner-border mb-3" />
                    <p className="text-muted mb-0">Gerando QR code...</p>
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ borderColor: 'var(--portal-border)' }}>
                <button className="btn btn-outline-secondary" onClick={() => setQrModal(null)}>Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
